import Link from 'next/link';
import type { ReactNode } from 'react';
import { Pagination, PaginationEllipsis, paginationControlVariants } from '@to/ui';
import type { VendorListItemVendor, VendorBadge } from './VendorListItem';
import { VendorListItem } from './VendorListItem';
import { VendorResultImpressions } from './VendorResultImpressions';

// ============================================================================
// Types
// ============================================================================

export interface VendorAugmentation {
  badges: VendorBadge[];
  hasSpecialOffer: boolean;
  offers?: import('@to/db/vendor').VendorDetailOffer[];
  offersUnavailable?: boolean;
}

export interface VendorSearchResultsProps {
  /** Vendor list to render */
  vendors: VendorListItemVendor[];
  /** Per-vendor augmentation keyed by vendor ID (string) */
  augmentations?: Map<string, VendorAugmentation>;
  /** Legacy input; all assigned image badges are always rendered. */
  showAllBadges?: boolean;
  /** Total vendor count (for "Showing X-Y of Z" text) */
  totalCount: number;
  /** Current page number (1-based) */
  page: number;
  /** Number of vendors per page */
  pageSize: number;
  /** Total number of pages */
  totalPages: number;
  /** Current search query (displayed in active filters) */
  query?: string;
  /** Currently selected category filters */
  selectedCategories?: string[];
  /** Currently selected location filters */
  selectedLocations?: string[];
  /** Base path for vendor detail links. Default: '/vendors' */
  vendorsPath?: string;
  /** Optional slot for rendering custom content above the vendor list */
  headerSlot?: ReactNode;
  /** Current sort mode — preserved in pagination URLs. Default: 'featured' */
  sort?: string;
  /** Optional slot for rendering a sort dropdown next to the results count */
  sortSlot?: ReactNode;
  /** App-owned first-party impression endpoint; required on every results surface. */
  impressionEndpoint: string;
  actionEndpoint: string;
  detailQuery?: string;
}

// ============================================================================
// Pagination helpers
// ============================================================================

function buildPageUrl(
  basePath: string,
  params: {
    q?: string;
    category?: string[];
    location?: string[];
    sort?: string;
  },
  page: number,
  contextQuery?: string,
): string {
  const searchParams = new URLSearchParams(contextQuery);
  if (params.q) searchParams.set('q', params.q);
  for (const cat of params.category ?? []) searchParams.append('category', cat);
  for (const loc of params.location ?? []) searchParams.append('location', loc);
  if (params.sort && params.sort !== 'featured') searchParams.set('sort', params.sort);
  if (page > 1) searchParams.set('page', String(page));
  const qs = searchParams.toString();
  return `${basePath}${qs ? `?${qs}` : ''}`;
}

function getPageNumbers(
  currentPage: number,
  totalPages: number,
): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage > 3) pages.push('ellipsis');

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (currentPage < totalPages - 2) pages.push('ellipsis');

  if (totalPages > 1) pages.push(totalPages);

  return pages;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Paginated vendor search results with active filter pills and pagination.
 * Accepts pre-fetched vendor data and renders VendorListItems with optional augmentation.
 * Server-safe — no client hooks or state.
 */
export function VendorSearchResults({
  vendors,
  augmentations,
  showAllBadges,
  totalCount,
  page,
  pageSize,
  totalPages,
  query,
  selectedCategories = [],
  selectedLocations = [],
  vendorsPath = '/vendors',
  sort,
  headerSlot,
  sortSlot,
  impressionEndpoint,
  actionEndpoint,
  detailQuery,
}: VendorSearchResultsProps) {
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);
  const hasActiveFilters = !!(query || selectedCategories.length > 0 || selectedLocations.length > 0);
  const pageNumbers = getPageNumbers(page, totalPages);
  const paginationParams = {
    q: query || undefined,
    category: selectedCategories.length > 0 ? selectedCategories : undefined,
    location: selectedLocations.length > 0 ? selectedLocations : undefined,
    sort: sort || undefined,
  };

  return (
    <div className="min-w-0 flex-1" style={{ fontFamily: 'var(--brand-font-body)' }}>
      {headerSlot}
      <h2 className="sr-only">Vendor results</h2>

      {/* Results count + sort */}
      {totalCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p
            aria-live="polite" aria-atomic="true"
            className="text-sm"
            style={{ color: 'var(--brand-input-placeholder)' }}
          >
            Showing {startIndex}&ndash;{endIndex} of {totalCount} vendor
            {totalCount !== 1 ? 's' : ''}
          </p>
          {sortSlot}
        </div>
      )}

      {/* Active filters summary */}
      {hasActiveFilters && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {query && (
            <span
              className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text-muted)',
              }}
            >
              Search: &ldquo;{query}&rdquo;
            </span>
          )}
          {selectedCategories.map((cat) => (
            <span
              key={`cat-${cat}`}
              className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text-muted)',
              }}
            >
              Category: {cat}
            </span>
          ))}
          {selectedLocations.map((loc) => (
            <span
              key={`loc-${loc}`}
              className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text-muted)',
              }}
            >
              Area: {loc}
            </span>
          ))}
          <Link
            href={`${vendorsPath}${detailQuery ?? ''}`}
            className="text-sm font-medium underline underline-offset-2 transition-colors"
            style={{ color: 'var(--brand-primary-text, var(--brand-primary))' }}
          >
            Clear all
          </Link>
        </div>
      )}

      {/* Vendor list */}
      {vendors.length > 0 ? (
        <VendorResultImpressions
          className="flex flex-col gap-4"
          endpoint={impressionEndpoint}
          actionEndpoint={actionEndpoint}
          viewKey={JSON.stringify([vendorsPath, query, selectedCategories, selectedLocations, sort, page, vendors.map((v) => v.id)])}
        >
          {vendors.map((vendor, index) => {
            const aug = augmentations?.get(String(vendor.id));
            return (
              <VendorListItem
                key={vendor.id}
                vendor={vendor}
                badges={aug?.badges}
                showAllBadges={showAllBadges}
                offers={aug?.offers}
                offersUnavailable={aug?.offersUnavailable}
                detailQuery={detailQuery}
                vendorsPath={vendorsPath}
                resultPosition={startIndex + index}
              />
            );
          })}
        </VendorResultImpressions>
      ) : (
        <div
          className="flex flex-col items-center justify-center rounded-lg border py-16"
          style={{
            borderColor: 'var(--brand-card-border)',
            backgroundColor: 'var(--brand-surface-subtle)',
          }}
        >
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--brand-text)' }}
          >
            No vendors found
          </p>
          <p
            className="mt-1 text-sm"
            style={{ color: 'var(--brand-input-placeholder)' }}
          >
            {hasActiveFilters
              ? 'Try adjusting your search or filters'
              : 'No vendors are listed in this directory yet'}
          </p>
          {hasActiveFilters && (
            <Link
              href={`${vendorsPath}${detailQuery ?? ''}`}
              className="mt-4 inline-flex items-center rounded-full px-5 py-2 text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--brand-button-bg, var(--brand-primary, #014F8A))' }}
            >
              Clear filters
            </Link>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="mt-10 gap-1">
          {page > 1 ? (
            <Link
              href={buildPageUrl(vendorsPath, paginationParams, page - 1, detailQuery)}
              className={paginationControlVariants({ className: 'rounded-full border' })}
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text)',
                backgroundColor: 'white',
              }}
              aria-label="Previous page"
            >
              Previous
            </Link>
          ) : (
            <span
              className={paginationControlVariants({ className: 'rounded-full border', current: false })}
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-input-placeholder)',
                backgroundColor: 'white',
              }}
              aria-disabled="true"
            >
              Previous
            </span>
          )}

          {pageNumbers.map((pageNum, idx) => {
            if (pageNum === 'ellipsis') {
              return (
                <PaginationEllipsis
                  key={`ellipsis-${idx}`}
                  style={{ color: 'var(--brand-input-placeholder)' }}
                />
              );
            }

            const isCurrent = pageNum === page;
            return (
              <Link
                key={pageNum}
                href={buildPageUrl(vendorsPath, paginationParams, pageNum, detailQuery)}
                className={paginationControlVariants({ current: isCurrent, className: 'rounded-full px-0' })}
                style={
                  isCurrent
                    ? { backgroundColor: 'var(--brand-button-bg, var(--brand-primary, #014F8A))', color: 'white' }
                    : { color: 'var(--brand-text)', backgroundColor: 'transparent' }
                }
                aria-label={`Page ${pageNum}`}
                aria-current={isCurrent ? 'page' : undefined}
              >
                {pageNum}
              </Link>
            );
          })}

          {page < totalPages ? (
            <Link
              href={buildPageUrl(vendorsPath, paginationParams, page + 1, detailQuery)}
              className={paginationControlVariants({ className: 'rounded-full border' })}
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-text)',
                backgroundColor: 'white',
              }}
              aria-label="Next page"
            >
              Next
            </Link>
          ) : (
            <span
              className={paginationControlVariants({ className: 'rounded-full border' })}
              style={{
                borderColor: 'var(--brand-card-border)',
                color: 'var(--brand-input-placeholder)',
                backgroundColor: 'white',
              }}
              aria-disabled="true"
            >
              Next
            </span>
          )}
        </Pagination>
      )}
    </div>
  );
}
